import { Model } from '../../../../types/model'
import { PrimaryAction, SupportingText } from '@ohgoodlord/design-system'
import { useState } from 'react'
import { OverlayComponentProps } from '../../types'
import { DefaultFooter } from '../default-footer'
import { createCountryCodeAlpha2, getCommaSeparatedAddress, toAddress } from './utils'
import { AddressSummary } from './address-summary'
import { FormInputs, ManualAddressEntry } from './manual-address-entry'
import { SubmitHandler, useForm } from 'react-hook-form'
import {
  Asterix,
  InputLabel,
  SelectWrapper,
  InputWrapper,
  FormWrapper,
  StyledContainer,
  StyledDescription,
} from './styles'

export interface Address {
  id: string
  name: string
  nameOrNumber: string
  line1: string
  line2?: string
  city: string
  county?: string
  country: string
  postcode: string
}

export const ADDRESS_CAPTURE = 'address-capture' as const

export interface AddressCaptureOverlayModel extends Model<typeof ADDRESS_CAPTURE> {
  overlayTitle: string
  addresses: Address[]
  id: string
  isManualEntry?: boolean
  nameLabel?: string
}

export type Props = OverlayComponentProps<AddressCaptureOverlayModel>

export const AddressCaptureOverlay = ({
  model,
  renderOverlayHeader,
  onOverlaySubmit,
  onSendEvent,
}: Props) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormInputs>()
  const initialAddress = model.addresses.find(address => address.id === model.id)

  const [selectedAddressValue, setSelectedAddressValue] = useState<string>(
    model.addresses.findIndex(address => address.id === model.id)?.toString() ?? '-1',
  )
  const [selectedAddress, setSelectedAddress] = useState<Address | undefined>(
    initialAddress !== undefined ? toAddress(initialAddress) : undefined,
  )

  const [roomNumber, setRoomNumber] = useState<string>('')
  const onSubmit: SubmitHandler<FormInputs> = manualEntryData => {
    const addressData =
      selectedAddressValue === 'ADDRESS_NOT_LISTED' || model.isManualEntry
        ? manualEntryData
        : selectedAddress

    if (addressData === undefined) {
      return
    }

    const formattedAddress = {
      ...addressData,
      countryCodeAlpha2: createCountryCodeAlpha2(addressData.country),
    }

    onSendEvent({
      name: 'form',
      value: { bedroomIdentifier: roomNumber, ...formattedAddress },
    })
    onOverlaySubmit()
  }

  return (
    <FormWrapper
      onSubmit={handleSubmit(onSubmit)}
      onKeyDown={event => {
        if (event.key === 'Enter') {
          event.preventDefault()
          handleSubmit(onSubmit)()
        }
      }}
    >
      <StyledContainer>
        {renderOverlayHeader !== undefined && renderOverlayHeader()}
        <StyledDescription>
          {!model.isManualEntry && (
            <>
              <SupportingText align="left">
                Select your address from the list below:
              </SupportingText>

              <SelectWrapper>
                <InputLabel $hasAsterix>
                  <SupportingText align="left">Address</SupportingText>
                  <Asterix>*</Asterix>
                </InputLabel>
                <select
                  onChange={e => {
                    setSelectedAddressValue(e.currentTarget.value)
                    const address = model.addresses[parseInt(e.currentTarget.value)]
                    setSelectedAddress(
                      address !== undefined ? toAddress(address) : undefined,
                    )
                  }}
                  value={selectedAddressValue}
                  data-testid="address-select"
                  id="addresses"
                >
                  <option value="-1">Select your address...</option>
                  <option value="ADDRESS_NOT_LISTED">My Address Isn't Listed</option>
                  {model.addresses.map((address, index) => (
                    <option key={index} value={index}>
                      {getCommaSeparatedAddress(address)}
                    </option>
                  ))}
                </select>
              </SelectWrapper>
            </>
          )}

          {selectedAddressValue !== 'ADDRESS_NOT_LISTED' && !model.isManualEntry && (
            <InputWrapper>
              <InputLabel>
                <SupportingText>Room Number (optional)</SupportingText>
              </InputLabel>
              <input
                value={roomNumber}
                onChange={e => setRoomNumber(e.currentTarget.value)}
                id="bedroomIdentifier"
                name="bedroomIdentifier"
              />
            </InputWrapper>
          )}

          {selectedAddress && <AddressSummary address={selectedAddress} />}
          {(selectedAddressValue === 'ADDRESS_NOT_LISTED' || model.isManualEntry) && (
            <ManualAddressEntry
              register={register}
              errors={errors}
              nameOrNumberLabel={model.nameLabel}
            />
          )}
        </StyledDescription>
      </StyledContainer>
      <DefaultFooter
        renderActions={() => (
          <PrimaryAction testId="modal-primary-action-button" type="submit" isFullWidth>
            Done
          </PrimaryAction>
        )}
      />
    </FormWrapper>
  )
}
